import { URLConfig } from '../components/global-nav/configs/base'
import { UserInformation } from '../components/global-nav/user-info/user-info'
import { Overlay } from '../components/overlay'
import { getAllRegionAuthConfigs, GbpeAllRegionAuthConfigs } from '../config/all-region-auth-configs'
import { FinalAuthConfig } from '../config/auth-config'
import { AuthStorageKeys } from '../config/auth-enumerations'
import { MYACCOUNT_LOADED } from '../events'
import { AuthContentService } from './auth-content-service'
import { AuthLinkService } from './auth-link-service'
import { HonoredFooterService } from './honored-footer-service'
import { HeadlessAuth } from './auth/authentication'
import { CustomClientApplication } from './auth/custom-client-application'
import { EntitlementService } from './auth/entitlement-service'
import { StandardClientApplication } from './auth/standard-client-application'
import { LanguageService } from './language-service'
import { RegionService } from './region-service'

export enum PortableExperiencesEvents {
	loaded = 'gbpeLoaded',
}

export interface PortableExperiences {
	auth: HeadlessAuth
	config: FinalAuthConfig
	languageService: LanguageService
	overlay: Overlay
	user: UserInformation
	regionService: RegionService
	entitlementService: EntitlementService
	authContentService: AuthContentService
	authLinkService: AuthLinkService
	honoredFooterService: typeof HonoredFooterService
	/** OAuth client IDs and B2C policies per region/locale for Production and Preproduction. */
	allConfigs: () => GbpeAllRegionAuthConfigs
	finalize: () => void
}

export type GetHeadlessAuthFunction = () => HeadlessAuth

export function getBaseHeadlessAuth(
	config: FinalAuthConfig,
	urlConfig: URLConfig,
	sessionStoragePrefix?: string
): HeadlessAuth {
	// Check storage for token. If we have external id token, a custom client application is used
	// Else, the standard client application is used
	const externalIdToken = sessionStorage.getItem(AuthStorageKeys.externalIdToken)

	if (externalIdToken) {
		return HeadlessAuth(new CustomClientApplication(urlConfig, config.authRequired), sessionStoragePrefix)
	} else {
		return HeadlessAuth(new StandardClientApplication(config, urlConfig, config.authRequired), sessionStoragePrefix)
	}
}

/**
 * Fires a callback if `window.gbpe` is loaded, otherwise adds an event listener and fires the callback when window.gbpe is loaded.
 * @param callback function to execute.
 */
export const gbpeLoadedCheck = (callback: (...args: any[]) => void): void => {
	if (window.gbpe) {
		callback()
	} else {
		window.addEventListener(PortableExperiencesEvents.loaded, () => callback(), { once: true })
	}
}

/**
 * Listens to the Myaccount loaded event and fires the call back
 * This is the closest indicator for the nav load
 * Use this without initializing globalNav object
 * @param callback function to execute.
 */
export const gbpeContentLoadedCheck = (callback: (...args: any[]) => void): void => {
	window.addEventListener(MYACCOUNT_LOADED, () => callback(), { once: true })
}

/**
 * Factory function that creates and returns a PortableExperiences object with configurable services.
 * This function initializes the core services needed for GBPE (GM Brand Digital Suite Portable Experiences)
 * including authentication, region/language services, overlays, and entitlements.
 *
 * @param _authConfig - Optional authentication configuration
 * @param _userInfo - Optional user information object
 * @param _headlessAuth - Optional headless authentication instance
 * @param _overlay - Optional overlay component instance
 * @param _urlConfig - Optional URL configuration
 * @param _entitlementService - Optional entitlement service instance
 * @param _authContentService - Optional authentication content service instance
 * @returns A PortableExperiences object with getters/setters for all services and a finalize method
 */
export const createPortableExperiences = (
	_authConfig?: FinalAuthConfig,
	_userInfo?: UserInformation,
	_headlessAuth?: HeadlessAuth,
	_overlay?: Overlay,
	_urlConfig?: URLConfig,
	_entitlementService?: EntitlementService,
	_authContentService?: AuthContentService
): PortableExperiences => {
	// Services passed in as parameters (mutable via setters)
	let _auth = _headlessAuth
	let _config = _authConfig

	// Services initialized within this factory
	let _regionService = new RegionService()
	let _languageService = new LanguageService(_urlConfig)
	const _authLinkService = new AuthLinkService()
	const _honoredFooterService = HonoredFooterService

	// Object that will be returned as the public API. Properties are defined via Object.defineProperties
	// to create controlled getter/setter access to private variables, enabling encapsulation of internal state.
	const publicMembers = {}

	// Private methods

	const _finalize = (): void => {
		// Prevent multiple finalizations by checking if object is already sealed
		// Revisit this line if we decide in the future to add/remove services post initialization
		if (!Object.isSealed(publicMembers) && !Object.isFrozen(publicMembers)) {
			Object.seal(publicMembers)
			window.dispatchEvent(new CustomEvent(PortableExperiencesEvents.loaded))
		}
	}

	const _allConfigs = (): GbpeAllRegionAuthConfigs => getAllRegionAuthConfigs()

	// Getters

	const getAuth = (): HeadlessAuth => _auth
	const getConfig = (): FinalAuthConfig => _config
	const getOverlay = (): Overlay => _overlay
	const getRegion = (): RegionService => _regionService
	const getLanguage = (): LanguageService => _languageService
	const getUser = (): UserInformation => _userInfo
	const getEntitlementService = (): EntitlementService => _entitlementService
	const getAuthContentService = (): AuthContentService => _authContentService
	const getAuthLinkService = (): AuthLinkService => _authLinkService
	const getHonoredFooterService = () => _honoredFooterService

	// Setters

	const setAuth = (auth: HeadlessAuth): void => {
		_auth = auth
	}
	const setConfig = (config: FinalAuthConfig): void => {
		_config = config
	}
	const setOverlay = (overlay: Overlay): void => {
		_overlay = overlay
	}
	const setRegion = (region: RegionService): void => {
		_regionService = region
	}
	const setLanguage = (languageService: LanguageService): void => {
		_languageService = languageService
	}
	const setUser = (userInfo: UserInformation): void => {
		_userInfo = userInfo
	}
	const setEntitlementService = (entitlementService: EntitlementService): void => {
		_entitlementService = entitlementService
	}
	const setAuthContentService = (authContentService: AuthContentService): void => {
		_authContentService = authContentService
	}

	Object.defineProperties(publicMembers, {
		finalize: { value: _finalize },
		allConfigs: { value: _allConfigs },
		auth: { get: getAuth, set: setAuth },
		config: { get: getConfig, set: setConfig },
		overlay: { get: getOverlay, set: setOverlay },
		regionService: { get: getRegion, set: setRegion },
		user: { get: getUser, set: setUser },
		languageService: { get: getLanguage, set: setLanguage },
		entitlementService: { get: getEntitlementService, set: setEntitlementService },
		authContentService: { get: getAuthContentService, set: setAuthContentService },
		authLinkService: { get: getAuthLinkService },
		honoredFooterService: { get: getHonoredFooterService },
	})

	return publicMembers as PortableExperiences
}
